import{runPrfCeremony,type StepUpProof}from"./webauthn";

const BASE="/api/v1/identity-access";
const AUTHORITY="/api/v1/security-authority";

export type Effect=
  |"ALLOW"
  |"DENY"
  |"STEP_UP"
  |"APPROVAL_REQUIRED"
  |"DUAL_APPROVAL_REQUIRED";

export interface PrincipalSummary{
  id:string;
  type:string;
  display_name:string;
  status:string;
  identities:Array<{type:string;email:string|null;verified:boolean}>;
  roles:string[];
  packages:string[];
}

export interface RoleSummary{
  role_key:string;
  display_name:string;
  class:string;
}

export interface PackageSummary{
  package_key:string;
  display_name:string;
  description:string|null;
}

export interface CapabilitySummary{
  capability_key:string;
  module_key:string;
  risk_class:string;
  required_assurance:number;
}

export interface LoginMethod{
  method_key:string;
  enabled:boolean;
  priority:number;
  implementation_status:string;
}

export interface LoginProfile{
  profile_key:string;
  display_name:string;
  audience:string;
  self_registration:boolean;
  min_assurance:number;
  session_max_age_seconds:number;
  default_role_key:string|null;
  version:number;
  methods:LoginMethod[];
}

export interface AccessEvent{
  id:number|string;
  event_type:string;
  target_type:string|null;
  target_key:string|null;
  outcome:string;
  created_at:string;
}

export interface ControlPlane{
  principals:PrincipalSummary[];
  roles:RoleSummary[];
  packages:PackageSummary[];
  capabilities:CapabilitySummary[];
  login_profiles:LoginProfile[];
  entitlements:unknown[];
  sod_rules:unknown[];
  recent_events:AccessEvent[];
}

export interface AssignmentDetail{
  assignment_id:string;
  role_key?:string;
  package_key?:string;
  source?:string;
  scope:Record<string,unknown>;
  starts_at?:string|null;
  expires_at?:string|null;
}

export interface OverrideDetail{
  override_id:string;
  capability:string;
  effect:Effect;
  scope:Record<string,unknown>;
  reason:string;
  expires_at:string|null;
}

export interface JitDetail{
  grant_id:string;
  capability:string;
  effect:Effect;
  scope:Record<string,unknown>;
  reason:string;
  expires_at:string;
}

export interface EffectiveAccess{
  capability:string;
  module:string;
  risk_class:string;
  decision:string;
  reason:string;
  required_assurance:number;
  entitlement_status:string|null;
  sources:Array<Record<string,unknown>>;
}

export interface PrincipalDetail{
  principal:{id:string;display_name:string;type:string;status:string};
  role_assignments:AssignmentDetail[];
  package_assignments:AssignmentDetail[];
  overrides:OverrideDetail[];
  jit:JitDetail[];
  effective_access:EffectiveAccess[];
}

export interface PendingRegistration{
  user_id:string;
  display_name:string;
  email:string;
  provider_key:string;
  state:"EMAIL_PENDING"|"OWNER_PENDING";
  email_verified_at:string|null;
  created_at:string;
  login_profile_key:string|null;
  email_verified:boolean;
}

export interface RegistrationApprovalResult{
  user_id:string;
  principal_id:string;
  state:"ACTIVE";
  login_profile_key:string;
  role_key:string|null;
  package_key:string|null;
}

async function req<T>(path:string,init?:RequestInit):Promise<T>{
  const r=await fetch(path,{
    credentials:"include",
    headers:{
      Accept:"application/json",
      "Content-Type":"application/json",
      ...(init?.headers??{}),
    },
    ...init,
  });

  if(!r.ok){
    let detail=
      r.status===401
        ?"authentication_required"
        :r.status===403
          ?"permission_denied"
          :`request_failed_${r.status}`;

    try{
      const body=await r.json();
      if(typeof body?.detail==="string")detail=body.detail;
    }catch{}

    throw new Error(detail);
  }

  return r.json() as Promise<T>;
}

// KNSAIX_EXPORTED_FRESH_STEP_UP_V1
export async function freshStepUpProof():Promise<StepUpProof>{
  const options=await req<{ceremony_id:string;publicKey:unknown}>(
    `${AUTHORITY}/step-up/options`,
    {method:"POST",body:"{}"},
  );

  return runPrfCeremony(options.ceremony_id,options.publicKey);
}

export interface MutationHooks{
  onPasskey?:()=>void;
  onApplying?:()=>void;
}

async function verifiedMutation<T>(
  path:string,
  body:Record<string,unknown>,
  hooks:MutationHooks={},
):Promise<T>{
  hooks.onPasskey?.();
  const proof=await freshStepUpProof();
  hooks.onApplying?.();

  return req<T>(`${BASE}${path}`,{
    method:"POST",
    body:JSON.stringify({...body,...proof}),
  });
}

export const identityAccessApi={
  controlPlane:()=>req<{control_plane:ControlPlane}>(`${BASE}/control-plane`),

  pendingRegistrations:()=>
    req<{registrations:PendingRegistration[]}>(
      `${BASE}/registrations/pending`,
    ),

  approveRegistration:(
    userId:string,
    loginProfileKey:string,
    roleKey:string|null,
    packageKey:string|null,
    reason:string,
    hooks:MutationHooks={},
  )=>verifiedMutation<RegistrationApprovalResult>(
    "/registrations/approve/verify",
    {
      user_id:userId,
      login_profile_key:loginProfileKey,
      role_key:roleKey,
      package_key:packageKey,
      reason,
    },
    hooks,
  ),


  principal:(id:string,assurance=3)=>
    req<{principal:PrincipalDetail}>(
      `${BASE}/principals/${encodeURIComponent(id)}?assurance=${assurance}`,
    ),

  simulate:(principalId:string,capability:string,assurance=3)=>
    req<{result:Record<string,unknown>}>(`${BASE}/simulate`,{
      method:"POST",
      body:JSON.stringify({
        principal_id:principalId,
        capability,
        assurance,
      }),
    }),

  assignRole:(
    principalId:string,
    key:string,
    reason:string,
    expiresAt:string|null=null,
    hooks:MutationHooks={},
  )=>verifiedMutation("/roles/assign/verify",{
    principal_id:principalId,key,scope:{},expires_at:expiresAt,reason,
  },hooks),

  revokeRole:(
    assignmentId:string,
    reason:string,
    hooks:MutationHooks={},
  )=>verifiedMutation("/roles/revoke/verify",{
      assignment_id:assignmentId,reason,
    },hooks),

  assignPackage:(
    principalId:string,
    key:string,
    reason:string,
    expiresAt:string|null=null,
    hooks:MutationHooks={},
  )=>verifiedMutation("/packages/assign/verify",{
    principal_id:principalId,key,scope:{},expires_at:expiresAt,reason,
  },hooks),

  revokePackage:(
    assignmentId:string,
    reason:string,
    hooks:MutationHooks={},
  )=>verifiedMutation("/packages/revoke/verify",{
      assignment_id:assignmentId,reason,
    },hooks),

  setOverride:(
    principalId:string,
    capability:string,
    effect:Effect,
    reason:string,
    expiresAt:string|null=null,
    hooks:MutationHooks={},
  )=>verifiedMutation("/overrides/set/verify",{
    principal_id:principalId,
    capability,
    effect,
    scope:{},
    expires_at:expiresAt,
    reason,
  },hooks),

  revokeOverride:(
    overrideId:string,
    reason:string,
    hooks:MutationHooks={},
  )=>verifiedMutation("/overrides/revoke/verify",{
      assignment_id:overrideId,reason,
    },hooks),

  assignJit:(
    principalId:string,
    capability:string,
    effect:Effect,
    expiresAt:string,
    reason:string,
    hooks:MutationHooks={},
  )=>verifiedMutation("/jit/assign/verify",{
    principal_id:principalId,
    capability,
    effect,
    scope:{},
    expires_at:expiresAt,
    reason,
  },hooks),

  revokeJit:(
    grantId:string,
    reason:string,
    hooks:MutationHooks={},
  )=>verifiedMutation("/jit/revoke/verify",{
      assignment_id:grantId,reason,
    },hooks),

  updateLoginProfile:(
    profileKey:string,
    body:{
      expected_version:number;
      self_registration:boolean;
      min_assurance:number;
      session_max_age_seconds:number;
      default_role_key:string|null;
      reason:string;
    },
    hooks:MutationHooks={},
  )=>verifiedMutation(
    `/login-profiles/${encodeURIComponent(profileKey)}/verify`,
    body,
    hooks,
  ),

  updateLoginMethod:(
    profileKey:string,
    methodKey:string,
    enabled:boolean,
    priority:number,
    reason:string,
    hooks:MutationHooks={},
  )=>verifiedMutation(
    `/login-profiles/${encodeURIComponent(profileKey)}/methods/verify`,
    {
      method_key:methodKey,
      enabled,
      priority,
      reason,
    },
    hooks,
  ),

  getPasswordStatus:(userId:string)=>
    req<{status:string}>(
      `${AUTHORITY}/users/${encodeURIComponent(userId)}/password`,
    ),

  setPassword: async(
    userId:string,
    password:string,
    hooks:MutationHooks={},
  )=>{
    hooks.onPasskey?.();
    const proof=await freshStepUpProof();
    hooks.onApplying?.();
    
    return req<{status:string;success?:boolean}>(
      `${AUTHORITY}/users/${encodeURIComponent(userId)}/password/reset/verify`,
      {
        method:"POST",
        body:JSON.stringify({
          ceremony_id:proof.ceremony_id,
          credential:proof.credential,
          prf_result:proof.prf_result,
          password,
        }),
      },
    );
  },

  disablePassword: async(userId:string, hooks:MutationHooks={})=>{
    hooks.onPasskey?.();
    const proof=await freshStepUpProof();
    hooks.onApplying?.();
    
    return req<{status:string;success?:boolean}>(
      `${AUTHORITY}/users/${encodeURIComponent(userId)}/password/disable/verify`,
      {
        method:"POST",
        body:JSON.stringify({
          ceremony_id:proof.ceremony_id,
          credential:proof.credential,
          prf_result:proof.prf_result,
        }),
      },
    );
  },
};
